import React, { useEffect, useState, KeyboardEvent, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search, Bell } from "lucide-react";
import Logout from "./Logout";
import {
  updateUserCredits,
  updateCreditSummary,
  setCategories,
  setCurrentUser,
  clearFlightSearch,
} from "../redux/actions";
import { Channel } from "../utils/Channel";
import SocketService from "../utils/SocketService";
import { LazyImage } from "../utils/helpers";
import { getSignInUrl } from "../utils/Constants";
import TermsCondition from "../modal/TermsCondition";
import { logout, setLoader } from "../redux/actions";
import "../../css/PageTopbarCss.css";
import { globalSearch } from "../request/Search";
import GlobalSearchResult from "../modal/GlobalSearchResult";
import type { GlobalSearchResults } from "../types/GlobalSearchType";
import { setToasterMessage } from "../redux/actions";
import { ListQueryParams } from "../interface/DataInterface";
import IdleInactivityModal from "../modal/IdleInactivityModal";
import createAxiosInstance from "../request/Axios";
import {
  getAllNotification,
  markedAllAsRead,
  markedAsRead,
  getUnreadCount,
} from "../request/Notifications";

// Define a notification type
interface Notification {
  id: number;
  description: string;
  created_at: string;
  is_read: boolean;
  filename: string;
  url?: string;
}

const PageTopbar: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.currentUser);
  const [terms, setTerms] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [gSearchResults, setgSearchResult] = useState<GlobalSearchResults>([]);
  const [showgSearchModal, setShowgSearchModal] = useState(false);

  // Notification states
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    //   if (process.env.REACT_APP_ENV !== "testing") {
    //     const socket = new SocketService(currentUser?.company?.id);
    //     socket.connect();

    //     socket.on(
    //       `${Channel.COMPANY}${currentUser?.company?.id}`,
    //       (data: any) => {
    //         dispatch(updateUserCredits(data));
    //       }
    //     );

    //     socket.on(`${Channel.ADMIN_CREDIT_SUMMARY}`, (data: any) => {
    //       dispatch(updateCreditSummary(data));
    //     });

    //     socket.on(
    //       `${Channel.CATEGORY_LIST}${currentUser?.company?.id}`,
    //       (data: any) => {
    //         dispatch(setCategories(data));
    //       }
    //     );

    //     socket.on(
    //       `${Channel.CURRENT_FUNDS}${currentUser?.company?.id}`,
    //       (data: any) => {
    //         const userD = data?.data !== undefined ? data.data : data;
    //         if (userD) {
    //           dispatch(setCurrentUser(userD));
    //         }
    //       }
    //     );

    //     socket.on(`${Channel.FORCE_LOGOUT}`, (data: any) => {
    //       dispatch(logout());
    //       dispatch(setLoader(false));
    //     });

    //     socket.on(
    //       `${Channel.FORCE_LOGOUT}:${currentUser?.company?.id}`,
    //       (data: any) => {
    //         console.log(`Logout: ${currentUser?.company?.id}`);
    //         dispatch(logout());
    //         dispatch(setLoader(false));
    //       }
    //     );

    //     socket.on(
    //       `${Channel.DOWNLOAD_ONWARD_ITINERARY}${currentUser?.company?.id}`,
    //       (data: any) => {
    //         console.log("DOWNLOAD_ONWARD_ITINERARY", data);
    //         addNotification(data);
    //         dispatch(
    //           setToasterMessage({
    //             isShow: true,
    //             header: "Download Itinerary",
    //             type: "success",
    //             message: "Itinerary ready for download",
    //           })
    //         );
    //       }
    //     );

    //   socket.on(
    //     `${Channel.DOWNLOAD_RETURNED_ITINERARY}${currentUser?.company?.id}`,
    //     (data: any) => {
    //       console.log("DOWNLOAD_RETURNED_ITINERARY", data);
    //       addNotification(data);
    //       dispatch(
    //         setToasterMessage({
    //           isShow: true,
    //           header: "Download Itinerary",
    //           type: "success",
    //           message: "Itinerary ready for download",
    //         })
    //       );
    //     }
    //   );
    // }

    const stored = localStorage.getItem("term_condition");
    const fallback = currentUser ? !currentUser.term_condition : true;

    setTerms(stored !== null ? stored === "1" : fallback);
  }, [currentUser, dispatch, setTerms]);

  // Inside your component:
  const getNotifications = useCallback(
    async (query: ListQueryParams = {}) => {
      getAllNotification(dispatch, query).then((res) => {
        if (res?.result.success && res?.result?.data.data) {
          setNotifications(res?.result?.data.data);
        }
      });
    },
    [dispatch]
  );

  const getUnreadCountNotification = useCallback(async () => {
    getUnreadCount(dispatch).then((res) => {
      if (res?.result.success && res?.result?.data) {
        setUnreadCount(res?.result?.data.total_unread);
      }
    });
  }, [dispatch]);

  // Now the effect won't re-run unless dispatch changes
  useEffect(() => {
    getNotifications();
    getUnreadCountNotification();
  }, [getNotifications, getUnreadCountNotification]);

  const addNotification = (notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
  };

  const markAsRead = (id: number) => {
    markedAsRead(dispatch, id).then((res) => {
      if (res?.result.success) {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === id ? { ...notif, is_read: true } : notif
          )
        );
        getUnreadCountNotification();
      }
    });
  };

  const markAllAsRead = () => {
    markedAllAsRead(dispatch).then((res) => {
      if (res?.result.success) {
        setNotifications((prev) =>
          prev.map((notif) => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
      }
    });
  };

  const handleCloseModal = () => {
    setTerms(false);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      triggerSearch();
    }
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const triggerSearch = async () => {
    if (searchValue.trim()) {
      dispatch(setLoader(true));
      await globalSearch(dispatch, { search: searchValue.trim() })
        .then((res: any) => {
          if (res?.result.success) {
            setgSearchResult(res?.result?.data);
          }
          setShowgSearchModal(true);
          dispatch(setLoader(false));
        })
        .catch((err) => {
          dispatch(setLoader(false));
        });
    }
  };

  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
  };

  // Format timestamp to a more readable format
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const logoutHandler = useCallback(async () => {
    try {
      dispatch(setLoader(true));
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post("/api/auth/logout");
      if (response.data.result.success) {
        await dispatch(setLoader(false));
        await dispatch(logout());
        await dispatch(clearFlightSearch());
        window.location.href = getSignInUrl();
      } else {
        dispatch(setLoader(false));
      }
    } catch (error) {
      dispatch(setLoader(false));
    }
  }, [dispatch]);

  return (
    <header id="page-topbar">
      <div className="layout-width">
        <div className="navbar-header">
          <IdleInactivityModal onLogout={logoutHandler} />
          {/* Left Section */}
          <div className="d-flex align-items-center">
            <GlobalSearchResult
              showModal={showgSearchModal}
              handleClose={() => setShowgSearchModal(false)}
              data={gSearchResults}
            />
            <TermsCondition showModal={terms} handleClose={handleCloseModal} />
            {/* LOGO */}
            <div className="navbar-brand-box horizontal-logo">
              <a href="/" className="logo logo-dark">
                <span className="logo-sm">
                  <img
                    src="https://cdn.mywanderlinx.com/assets/images/mjc-logo.png"
                    alt="Logo"
                    height="22"
                  />
                </span>
                <span className="logo-lg">
                  <img
                    src="https://cdn.mywanderlinx.com/assets/images/mjc-logo.png"
                    alt=""
                    height="17"
                  />
                </span>
              </a>

              <a href="/" className="logo logo-light">
                <span className="logo-sm">
                  <img
                    src="https://cdn.mywanderlinx.com/assets/images/mjc-logo.png"
                    alt=""
                    height="22"
                  />
                </span>
                <span className="logo-lg">
                  <img
                    src="https://cdn.mywanderlinx.com/assets/images/mjc-logo.png"
                    alt=""
                    height="17"
                  />
                </span>
              </a>
            </div>

            <button
              type="button"
              className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
              id="topnav-hamburger-icon"
            >
              <span className="hamburger-icon">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>

          {/* Center Search - Desktop */}
          {currentUser?.roles === "Admin" && (
            <div className="d-none d-md-flex flex-grow-1 justify-content-center px-4">
              <div className="search-box">
                <div className="search-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={handleSearch}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className="search-button"
                    onClick={triggerSearch}
                    title="Search"
                  >
                    <Search className="search-icon" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Right Section */}
          <div className="d-flex align-items-center">
            {/* Search Icon - Mobile */}
            <div className="d-md-none me-2">
              <button className="mobile-search-toggle" onClick={toggleSearch}>
                <Search className="search-icon-mobile" />
              </button>
            </div>

            {/* Notification Bell */}
            <div className="dropdown notification-dropdown">
              <button
                className="notification-bell-button dropdown-toggle"
                type="button"
                id="notificationDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                onClick={toggleNotifications}
              >
                <Bell className="notification-bell-icon" size={20} />
                {unreadCount > 0 && (
                  <span className="notification-badge position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              <div
                className="dropdown-menu dropdown-menu-end notification-dropdown-menu"
                aria-labelledby="notificationDropdown"
              >
                <div className="notification-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 dropdown-header">Notifications</h6>
                  {unreadCount > 0 && (
                    <button
                      className="btn btn-sm btn-link text-decoration-none dropdown-header-action"
                      onClick={markAllAsRead}
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="notification-scrollable-area">
                  {notifications.length === 0 ? (
                    <div className="dropdown-item empty-notification text-center text-muted">
                      No notifications available
                    </div>
                  ) : (
                    <>
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`dropdown-item notification-item ${
                            !n.is_read ? "unread" : ""
                          }`}
                          onClick={() => markAsRead(n.id)}
                        >
                          <div className="notification-content">
                            <p className="notification-message mb-1">
                              {n.description}
                            </p>
                            <small className="notification-time d-block">
                              {formatTime(n.created_at)}
                            </small>
                          </div>
                          {n.url && (
                            <a
                              href={n.url}
                              className="notification-action btn btn-sm btn-outline-primary mt-1"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Download
                            </a>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {/* {notifications.length > 0 && (
                  <div className="dropdown-divider"></div>
                )}
                <a
                  className="dropdown-item text-center see-all-link"
                  href="/notifications"
                >
                  See all notifications
                </a> */}
              </div>
            </div>

            <span className="d-block d-md-none fs-12 text-muted user-name-sub-text">
              Account ID: <br /> {currentUser?.account_id ?? 0}
            </span>
            <div className="dropdown ms-sm-3 header-item topbar-user pl-1">
              <span className="d-flex align-items-center ml-2">
                <div className="position-relative">
                  <LazyImage
                    src={currentUser?.profile}
                    alt={`Profile picture of ${
                      currentUser?.fname + " " + currentUser?.lname
                    }`}
                    imgSrc={currentUser?.profile}
                    className="rounded-circle header-profile-user"
                  />
                </div>
                <span className="text-start ms-xl-2">
                  <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                    {currentUser?.fname + " " + currentUser?.lname}
                  </span>
                  <span className="d-none d-md-block d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                    {`Account ID: ${currentUser?.account_id ?? 0}`}
                  </span>
                </span>
              </span>
              <Logout />
            </div>
          </div>
        </div>

        {/* Mobile Search Input */}
        {currentUser?.roles === "Admin" && isSearchVisible && (
          <div className="mobile-search-container">
            <div className="position-relative">
              <input
                type="text"
                className="mobile-search-input"
                placeholder="Search..."
                value={searchValue}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
              />
              <button
                className="mobile-search-button"
                onClick={triggerSearch}
                title="Search"
              >
                <Search className="search-icon-mobile" />
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default PageTopbar;
